import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
    Box,
    Card,
    Typography,
    Grid,
    Avatar,
    Button,
    styled,
    useTheme,
    useMediaQuery,
    Fab,
    Zoom,
    Tooltip,
    Stack,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper
} from "@mui/material";

/* ✅ Icons */
import { AiFillBulb, AiOutlineRead } from "react-icons/ai";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CodeIcon from "@mui/icons-material/Code";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import StopIcon from "@mui/icons-material/Stop";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";

/* ✅ Assets */
const bgImage = "https://images.unsplash.com/photo-1579403124614-197f69d8187b"; // JS Focused Abstract

/* ✅ Styled Components - Premium Dark Glass Theme */
const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(16,7,32,0.9), rgba(16,7,32,0.95)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
    [theme.breakpoints.down("sm")]: {
        paddingTop: theme.spacing(8),
    },
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(16,7,32,0.85)",
    borderRadius: "14px",
    padding: theme.spacing(3),
    marginBottom: "25px",
    boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
    border: "1px solid rgba(255,255,255,0.1)",
    color: "white",
}));

const CodeBlock = styled("pre")(({ theme }) => ({
    backgroundColor: "#0d1117",
    color: "#c9d1d9",
    padding: "20px",
    borderRadius: "10px",
    overflowX: "auto",
    fontSize: "0.9rem",
    lineHeight: 1.6,
    border: "1px solid rgba(255,255,255,0.05)",
    fontFamily: "'Fira Code', 'Courier New', monospace",
}));

const ActionButton = styled(Button)(({ theme }) => ({
    backgroundColor: "goldenrod",
    color: "#100720",
    fontWeight: 800,
    padding: "12px 40px",
    borderRadius: "999px",
    transition: "0.3s",
    "&:hover": {
        backgroundColor: "#e0b200",
        transform: "scale(1.05)",
    },
}));

/**
 * JavaScriptDataTypes Component
 * * Explains JS Primitives vs Objects.
 * Features: Responsive table, TTS accessibility, and clipboard utilities.
 */
export default function JavaScriptDataTypes() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    /* ✅ UI State Management */
    const [showScroll, setShowScroll] = useState(false);
    const [copiedIndex, setCopiedIndex] = useState(null);
    const [isSpeaking, setIsSpeaking] = useState(false);

    /* ✅ FAB Visibility Logic */
    useEffect(() => {
        const checkScroll = () => setShowScroll(window.pageYOffset > 400);
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, []);

    /* ✅ Audio Controls */
    const handleSpeak = (text) => {
        window.speechSynthesis.cancel();
        if (isSpeaking) {
            setIsSpeaking(false);
            return;
        }
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.rate = 0.95;
        utterance.onstart = () => setIsSpeaking(true);
        utterance.onend = () => setIsSpeaking(false);
        window.speechSynthesis.speak(utterance);
    };

    const stopSpeech = () => {
        window.speechSynthesis.cancel();
        setIsSpeaking(false);
    };

    /* ✅ Utility: Copy Code */
    const handleCopy = (text, index) => {
        navigator.clipboard.writeText(text);
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
    };

    /* ✅ Educational Content */
    const jsDataModules = useMemo(() => [
        {
            title: "Primitive Types",
            tag: "Immutable Values",
            desc: "Primitives are the most basic types in JavaScript. They are immutable (cannot be changed) and are passed by value. There are 7 primitives, including String, Number, and Boolean.",
            code: `let name = "Sarah";    // String\nlet age = 30;         // Number\nlet isCoding = true;  // Boolean\nlet result = null;    // Null\nlet undefinedVar;     // Undefined`
        },
        {
            title: "Loosely Typed Nature",
            tag: "Dynamic Typing",
            desc: "JavaScript is a 'loosely typed' or dynamic language. You don't need to declare the type of a variable, and variables can change types automatically during execution.",
            code: `let data = 42;       // data is a Number\ndata = "Hello";      // now data is a String\ndata = true;         // now data is a Boolean`
        }
    ], []);

    return (
        <PageWrapper>
            {/* 🏆 Header Section */}
            <Box sx={{ textAlign: "center", mb: 6, px: 2 }}>
                <Typography
                    variant={isMobile ? "h4" : "h3"}
                    sx={{
                        fontWeight: 800,
                        backgroundColor: "goldenrod",
                        color: "#100720",
                        px: { xs: 3, md: 6 },
                        py: 1.5,
                        borderRadius: "999px",
                        display: "inline-block",
                        boxShadow: "0 6px 20px rgba(218, 165, 32, 0.3)",
                    }}
                >
                    JS LEARNING PATH
                </Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
                {/* 🎯 Introduction Section */}
                <Grid item xs={12}>
                    <SectionCard>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                            <Typography variant="h5" fontWeight={800} color="goldenrod">
                                02. Understanding Data Types
                            </Typography>
                            <IconButton
                                onClick={() => handleSpeak("In JavaScript, data types describe the different kinds of data that we work with and store in variables. Modern JS categorizes these into Primitives and Objects.")}
                                sx={{ color: "goldenrod" }}
                            >
                                <VolumeUpIcon />
                            </IconButton>
                        </Stack>
                        <Typography variant="body1" sx={{ opacity: 0.9, lineHeight: 1.7 }}>
                            Every piece of data in JavaScript has a type. Understanding these is vital because it determines what operations you can perform (e.g., you can't divide a string by a boolean). Modern JS classifies data into <b>Primitives</b> (simple values) and <b>Objects</b> (complex collections).
                        </Typography>

                        <TableContainer component={Paper} sx={{ mt: 4, backgroundColor: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.1)" }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ color: "goldenrod", fontWeight: "bold" }}>Type</TableCell>
                                        <TableCell sx={{ color: "goldenrod", fontWeight: "bold" }}>Purpose</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {[
                                        { t: "String", p: "Text sequences / Characters" },
                                        { t: "Number", p: "Integers and floating-point decimals" },
                                        { t: "Boolean", p: "Logical true or false" },
                                        { t: "Null", p: "Intentional absence of value" },
                                        { t: "Undefined", p: "Variable declared but not assigned" }
                                    ].map((row) => (
                                        <TableRow key={row.t}>
                                            <TableCell sx={{ color: "white", borderBottom: "1px solid rgba(255,255,255,0.05)" }}><b>{row.t}</b></TableCell>
                                            <TableCell sx={{ color: "white", opacity: 0.8, borderBottom: "1px solid rgba(255,255,255,0.05)" }}>{row.p}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </SectionCard>
                </Grid>

                {/* ✅ Content Rendering */}
                {jsDataModules.map((item, i) => (
                    <Grid item xs={12} key={i}>
                        <SectionCard>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                    <Avatar sx={{ bgcolor: "rgba(218, 165, 32, 0.2)", color: "goldenrod" }}>
                                        <AiFillBulb size={24} />
                                    </Avatar>
                                    <Box>
                                        <Typography variant="h6" fontWeight={700}>{item.title}</Typography>
                                        <Typography variant="caption" color="goldenrod" fontWeight={800}>{item.tag}</Typography>
                                    </Box>
                                </Box>

                                <Stack direction="row" spacing={1}>
                                    <Tooltip title="Listen">
                                        <IconButton onClick={() => handleSpeak(`${item.title}. ${item.desc}`)} sx={{ color: "goldenrod" }}>
                                            <VolumeUpIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title={copiedIndex === i ? "Copied!" : "Copy Code"}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => handleCopy(item.code, i)}
                                            sx={{ color: "white", borderColor: "rgba(255,255,255,0.3)" }}
                                        >
                                            {copiedIndex === i ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                                        </Button>
                                    </Tooltip>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<PlayCircleOutlineIcon />}
                                        href="https://playcode.io/javascript"
                                        target="_blank"
                                        sx={{ color: "goldenrod", borderColor: "goldenrod" }}
                                    >
                                        Try it
                                    </Button>
                                </Stack>
                            </Box>

                            <Typography variant="body1" sx={{ mb: 3, opacity: 0.9, lineHeight: 1.6 }}>{item.desc}</Typography>
                            <CodeBlock>{item.code}</CodeBlock>
                        </SectionCard>
                    </Grid>
                ))}

                {/* ✅ Pro Tip Section */}

                <Grid item xs={12}>
                    <Box sx={{
                        p: 3,
                        borderRadius: "14px",
                        backgroundColor: "rgba(218, 165, 32, 0.1)",
                        borderLeft: "6px solid goldenrod",
                        display: "flex",
                        alignItems: "center",
                        gap: 2
                    }}>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: "goldenrod" }}>
                            💡 Pro Tip: JavaScript is "Loosely Typed," meaning variables can change types on the fly! This provides flexibility but requires careful management to avoid unexpected bugs.
                        </Typography>
                    </Box>
                </Grid>

                {/* ✅ Navigation Footer */}
                <Grid item xs={12} textAlign="center" sx={{ mt: 4, mb: 10 }}>
                    <Stack direction="row" spacing={2} justifyContent="center">
                        <Link to="/variable-declaration" style={{ textDecoration: "none" }}>
                            <Button variant="outlined" sx={{ color: "white", borderColor: "white", borderRadius: "999px", px: 4 }}>
                                PREVIOUS LESSON
                            </Button>
                        </Link>
                        <Link to="/functions" style={{ textDecoration: "none" }}>
                            <ActionButton variant="contained">
                                NEXT: FUNCTIONS
                            </ActionButton>
                        </Link>
                    </Stack>
                </Grid>
            </Grid>

            {/* ✅ Floating Control FABs */}
            <Stack spacing={2} sx={{ position: "fixed", bottom: 25, right: 25, zIndex: 1000 }}>
                {isSpeaking && (
                    <Zoom in={isSpeaking}>
                        <Fab size="medium" color="error" onClick={stopSpeech}>
                            <StopIcon />
                        </Fab>
                    </Zoom>
                )}
                <Zoom in={showScroll}>
                    <Fab
                        size="small"
                        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                        sx={{ bgcolor: "goldenrod", "&:hover": { bgcolor: "#e0b200" } }}
                    >
                        <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                    </Fab>
                </Zoom>
            </Stack>
        </PageWrapper>
    );
}
